import { useEffect, useRef } from 'react';
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interaction from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import { Card, CardBody, CardFooter } from 'reactstrap';
import { VisitCalendarEvent } from '../adapters/calendarEventsAdapter';

interface Props {
  events: VisitCalendarEvent[];
  selectedVisitId?: string | null;
  // Clic en una visita: abre su detalle.
  onOpenDetail: (visitId: string) => void;
  footer?: React.ReactNode;
}

// Calendario de sólo lectura del dashboard (PreVenta, Renta y Secondary).
const DashboardVisitsCalendar = ({
  events,
  selectedVisitId,
  onOpenDetail,
  footer,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<Calendar | null>(null);
  const onOpenDetailRef = useRef(onOpenDetail);
  onOpenDetailRef.current = onOpenDetail;

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const cal = new Calendar(containerRef.current, {
      plugins: [interaction, dayGridPlugin],
      initialView: 'dayGridMonth',
      height: 'auto',
      locale: esLocale,
      firstDay: 1,
      buttonText: { today: 'Hoy' },
      headerToolbar: { left: 'title', right: 'today prev,next' },
      showNonCurrentDates: false,
      dayMaxEvents: 3,
      eventDisplay: 'block',
      eventClick: (arg) => onOpenDetailRef.current(arg.event.id),
    });
    cal.render();
    calendarRef.current = cal;
    return () => {
      cal.destroy();
      calendarRef.current = null;
    };
  }, []);

  // Los eventos se vuelven a armar en cada render del dashboard: sólo se
  // repintan cuando cambia su contenido.
  const eventsKey = events
    .map((e) => `${e.id}|${e.start}|${e.color}|${e.id === selectedVisitId}`)
    .join(',');

  useEffect(() => {
    const cal = calendarRef.current;
    if (!cal) return;
    cal.removeAllEvents();
    events.forEach((event) =>
      cal.addEvent({
        id: event.id,
        title: event.title,
        start: event.start,
        backgroundColor: event.color,
        borderColor: event.color,
        classNames: event.id === selectedVisitId ? ['is-selected'] : [],
      }),
    );
    // Lleva el calendario al mes de la visita seleccionada.
    const selected = events.find((event) => event.id === selectedVisitId);
    if (selected) cal.gotoDate(selected.start);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsKey]);

  return (
    <Card className="dv2-calendar">
      <CardBody>
        <div ref={containerRef} />
      </CardBody>
      {footer && <CardFooter>{footer}</CardFooter>}
    </Card>
  );
};

export default DashboardVisitsCalendar;
